import React, {useContext} from 'react';
import PropTypes from 'prop-types';
import {Button, Table, notification, Statistic} from 'antd';
import {useDispatch, useSelector} from "react-redux";
import Icon from '@web/components/Icon';
import {UserContext} from '@web/hooks/context';
import CollectionHeader from './CollectionHeader';
import {formatField} from '@web/lib/formaters';
import {ServerTimestamp, updatePath} from '@web/lib/firestore.db';
import collectionsActions from '@web/redux/collections/actions';
import {api} from '@web/lib/api';
import {notifyAndTrack} from './notification';
import {useLocation} from 'wouter';
import now from 'lodash/now';
// import dayjs from 'dayjs';
import uniq from 'lodash/uniq';

DataTable.propTypes = {
  collection: PropTypes.object,
  // list: PropTypes.array,
  table: PropTypes.array,
  collectionId: PropTypes.string,
  collectionPath: PropTypes.string,
  bordered: PropTypes.bool,
  // dataPath: PropTypes.string,
};

function DataTable(props) {
  const [userId, h] = useContext(UserContext);
  const dispatch = useDispatch();
  const [location, setLocation] = useLocation();
  const selectedRowKeys = useSelector((state) => state.Collections.selectedPaths?.[props.collectionId]);

  React.useEffect(() => {
    updatePath(`user/${userId}/collections/${props.collectionId}`, {
      viewed: ServerTimestamp(),
    }, true);
  }, [props.collectionId]);

  const onClickEndpoint = ({endpoint, params, value = {}, record = {}}) => {
    if (params.url) {
      // use template string to replace path
      const url = params.url.replace(/{{([^}]+)}}/g, (_, key) => {
        return record[key] || value[key];
      });
      window.open(url, "_blank");
      return;
    }

    if (params.path && !params.method) {
      // use template string to replace path
      const path = params.path.replace(/{{([^}]+)}}/g, (_, key) => {
        return record[key] || value[key];
      });
      return setLocation(`/${path}`);
    }

    console.log("onClickEndpoint", {endpoint, params, value, record});

    switch (params.method) {
      case "DELETE":
        console.log("onClickEndpoint:DELETE", record.objectID);
        params.host === "item" ?
        api.v2({
          ...params || null,
          path: [h, record.objectID].join("/"),
        }) :
        api.v2({
          ...params || null,
          path: [h, props.collectionId].join("/"),
        });
        break;
      default: { // todo - This is untested
        const path = params.path &&
          params.path.replace(/{{([^}]+)}}/g, (_, key) => {
            return record[key] || value[key];
          });

        api.v2({
          path: path ?
            [h, path].join("/") :
            [h, value.key, endpoint].join("/"),
          body: {...record || null},
          ...params || null,
        })
        .then((response) => {
          if (response instanceof Error) {
            console.log("onClickEndpoint:error", response);
            return notification.warn({message: "something went wrong"});
          }
          notifyAndTrack
          .info(response.authUrl, response.instructions, {
            title: "Click to authorize",
            duration: 0,
            onClick: () => {
              window.open(response.authUrl, "_blank");
            },
          })});
      }
    }
  }

  const getActions = (actionsForHost, options) => {
    return {
      title: '',
      dataIndex: '',
      key: 'x',
      fixed: "right",
      render: (value, record) => <Button.Group size="small">
        {Object
        .entries(props.collection?.endpoints || {})
        .filter(([, params]) => params.host === actionsForHost)
        .map(([endpoint, params]) => {
          return <Button
            // ghost
            key={value.key + endpoint}
            type="link"
            {...options || null}
            onClick={() =>
              onClickEndpoint({
                endpoint,
                params,
                value,
                record,
              })}
          >
            {params.icon ?
              <Icon name={params.icon} /> :
              endpoint
            }
          </Button>;
        })}
      </Button.Group>,
    };
  }

  const getTitle = () => {
    return <CollectionHeader
      collectionId={props.collectionId}
      collection={props.collection}
      onClickEndpoint={onClickEndpoint}
    />;
  }

  const customRender = (key, value, record) => {
    const nextTs = new Date(value).getTime();
    // console.log("key", dayjs(value).valueOf(), nextTs, record.timezone);
    // const lastTs = new Date(record.last).getTime();
    // const percent = Math.floor(((now() - lastTs) / (nextTs - lastTs)) * 100);
    return nextTs >= now() ?
      <Statistic.Countdown title={value} value={nextTs} onFinish={() => null} /> :
      <Statistic.Countdown title={value} value={nextTs} onFinish={() => null} />;
    // return <Progress
    //   type="line"
    //   style={{width: 200}}
    //   percent={percent}
    //   strokeWidth={10}
    //   strokeColor="rgba(255, 255, 255, 0.75)"
    //   format={() =>
    //     <Statistic.Countdown title="Next" value={nextTs} onFinish={() => null} />}
    // />
  }

  const toColumn = (key, title, fixed) => {
    return {
      title: title.split(".").pop(),
      dataIndex: key,
      key: key,
      fixed: fixed ? "left" : undefined,
      render: (value, record) => {
        return key !== "next" ?
          <span key={key}>{formatField(key, value)}</span> :
          customRender(key, value, record);
          //<DataTableCol field={key} objectID={record} dataPath={props.dataPath} />; //  typeof value === 'object' ? <JsonIcon /> : <label className="line-clamp-1 muted">{value}</label>;
      },
    };
  }

  const onSelectChange = React.useCallback((newSelectedRowKeys) => {
    dispatch(collectionsActions.setSelectedPaths(props.collectionId, newSelectedRowKeys));
  }, [dispatch, props.collectionId]);

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const getColumns = () => {
    return uniq([
      "objectID",
      props.collection?.defaultDisplayedFields ||
      Object.keys(props.collection?.schema?.properties || props.table?.[0] || {}),
    ].flat())
    .reduce(($acc, key) => {
      if (key === "key") {
        return $acc;
      }

      if (key === "objectID") {
        if (!props.collection?.showObjectId) {
          return $acc;
        }
        return $acc.columns.push({
          title: "ID",
          dataIndex: "objectID",
          key: key,
          fixed: "left",
          render: (value, record) => <span className="muted line-clamp-1" style={{maxWidth: 400}}>{record.objectID}</span>,
        }) && $acc;
      }

      const [rootKey, ...title] = key.split(".");
      const fixed = false; // index === 0;
      if (rootKey === $acc.current?.title) {
        $acc.current.children.push(toColumn(key, title.join("."), fixed));
      } else {
        $acc.current = title?.length ?
          {
            ...toColumn(key, rootKey, fixed),
            children: [toColumn(key, title.join("."), fixed)],
          } :
          {...toColumn(key, rootKey, fixed)};
        $acc.columns.push($acc.current);
      }
      return $acc;
    }, {current: {}, columns: []})
    .columns
    .concat(getActions("item"));
  }

  // console.log("DataTable:props:", props.collectionId, props.table);

  return <div style={{width: "98%", flex: 'none', margin: "0 auto"}}>
    <Table
      title={getTitle}
      bordered={!!props.bordered}
      loading={false}
      dataSource={props.table || []}
      rowSelection={rowSelection}
      size="small"
      columns={getColumns()}
      pagination={false}
      scroll={{
        x: "max-content", // 600,
        // y: 1260,
      }}
      footer={() => <div>line.ai</div>}
    />
  </div>;
}

export default DataTable;
